import React, { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { LogoLockup } from "./Logo";

/**
 * <AppGate>
 *
 * A simple, deliberately-minimal access gate that sits in front of the
 * entire platform. Until the visitor types the correct credentials, no
 * route renders.
 *
 * Scope notes:
 *   • This is NOT real authentication. Credentials are hard-coded in JS
 *     and visible in the bundle — any technical user with browser dev
 *     tools can read them or call /api/... directly. Use it only to
 *     keep accidental visitors / search-engine crawlers off the demo.
 *   • Persistence is a single localStorage flag (`wn.gate.unlocked.v2`).
 *     Clearing site data resets the gate.
 *   • This gate is independent from the in-app role picker (signed-in
 *     role lives in `wn.session.v1`). After unlock, the visitor still
 *     lands on the public Landing page and chooses a role like normal.
 */

const STORAGE_KEY = "wn.gate.unlocked.v2";

// Hard-coded gate credentials. If you change these, also rotate the
// localStorage flag if you want everyone re-prompted on next visit.
const GATE_USER = "admin.whole";
const GATE_PASS = "Whole@12345";

function isUnlocked() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "yes";
  } catch {
    return false;
  }
}

/**
 * Escape hatch: if the URL has `?lock=1`, the gate is immediately
 * re-armed (storage flag cleared) at the very first render — before
 * any unlocked content has a chance to paint. The query param is
 * stripped from the URL right after. This lets a presenter share or
 * bookmark a `…?lock=1` link to put the gate back in front of any
 * stale-localStorage browser without DevTools.
 */
function consumeLockQueryIfPresent() {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("lock") !== "1") return false;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable */
    }
    sp.delete("lock");
    const query = sp.toString();
    const cleaned =
      window.location.pathname +
      (query ? `?${query}` : "") +
      window.location.hash;
    window.history.replaceState({}, "", cleaned);
    return true;
  } catch {
    return false;
  }
}

export default function AppGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    // Consume `?lock=1` synchronously on first render so the gate
    // shows on the first paint — no flicker of unlocked content.
    if (consumeLockQueryIfPresent()) return false;
    return isUnlocked();
  });

  if (unlocked) {
    return children;
  }

  return (
    <GateScreen
      onUnlock={() => {
        try {
          localStorage.setItem(STORAGE_KEY, "yes");
        } catch {
          /* localStorage unavailable — gate becomes per-tab only */
        }
        setUnlocked(true);
      }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function GateScreen({ onUnlock }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr("");
    // A tiny artificial delay so a casual scripted brute-force is at
    // least mildly annoying. Real rate-limiting belongs on the backend.
    setTimeout(() => {
      if (u.trim() === GATE_USER && p === GATE_PASS) {
        onUnlock();
      } else {
        setErr("Those credentials don't match. Please try again.");
        setBusy(false);
      }
    }, 450);
  };

  return (
    <div
      data-testid="app-gate"
      style={{
        minHeight: "100vh",
        background: "#13163C",
        color: "#FBFAFD",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        position: "relative",
      }}
    >
      {/* Decorative grain overlay */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 25% 20%, rgba(177,78,47,0.10) 0, transparent 60%), radial-gradient(circle at 80% 90%, rgba(251,250,253,0.05) 0, transparent 55%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          width: "100%",
          maxWidth: 440,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div className="flex justify-center" style={{ marginBottom: 28 }}>
          <LogoLockup tone="white" size={36} />
        </div>

        <div
          style={{
            background: "rgba(251,250,253,0.04)",
            border: "1px solid rgba(251,250,253,0.14)",
            borderRadius: 6,
            padding: "32px 28px",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center gap-3" style={{ marginBottom: 6 }}>
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 28,
                height: 2,
                background: "#B14E2F",
              }}
            />
            <span
              className="wn-label"
              style={{ color: "#E8A07F", letterSpacing: "0.12em" }}
            >
              Private preview
            </span>
          </div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: 28,
              lineHeight: 1.15,
              fontWeight: 500,
              letterSpacing: "-0.01em",
              marginBottom: 8,
              color: "#FBFAFD",
            }}
          >
            Sign in to continue.
          </h1>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              fontSize: 15,
              color: "rgba(251,250,253,0.72)",
              marginBottom: 24,
              lineHeight: 1.55,
            }}
          >
            This is an invitation-only preview of WHOLE Neighborhoods.
            If you have your credentials, enter them below.
          </p>

          <form onSubmit={submit} data-testid="app-gate-form">
            <div style={{ marginBottom: 14 }}>
              <label
                className="wn-label"
                style={{
                  color: "rgba(251,250,253,0.72)",
                  display: "block",
                  marginBottom: 6,
                }}
                htmlFor="gate-user"
              >
                Username
              </label>
              <input
                id="gate-user"
                type="text"
                autoComplete="username"
                value={u}
                onChange={(e) => setU(e.target.value)}
                disabled={busy}
                autoFocus
                data-testid="app-gate-username"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label
                className="wn-label"
                style={{
                  color: "rgba(251,250,253,0.72)",
                  display: "block",
                  marginBottom: 6,
                }}
                htmlFor="gate-pass"
              >
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="gate-pass"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  value={p}
                  onChange={(e) => setP(e.target.value)}
                  disabled={busy}
                  data-testid="app-gate-password"
                  style={{ ...inputStyle, paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  data-testid="app-gate-toggle-password"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    color: "rgba(251,250,253,0.55)",
                    cursor: "pointer",
                    padding: 6,
                    display: "inline-flex",
                  }}
                >
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {err && (
              <div
                role="alert"
                data-testid="app-gate-error"
                style={{
                  background: "rgba(177,78,47,0.18)",
                  border: "1px solid rgba(177,78,47,0.40)",
                  color: "#F5C7B6",
                  padding: "10px 12px",
                  borderRadius: 4,
                  fontSize: 13,
                  marginBottom: 14,
                  lineHeight: 1.5,
                }}
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !u || !p}
              data-testid="app-gate-submit"
              style={{
                width: "100%",
                background: busy ? "#8F3C23" : "#B14E2F",
                color: "#FBFAFD",
                border: "1px solid #B14E2F",
                padding: "13px 16px",
                fontSize: 13.5,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                borderRadius: 4,
                cursor: busy ? "wait" : "pointer",
                opacity: !u || !p ? 0.55 : 1,
                transition: "background-color 160ms ease, opacity 160ms ease",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Lock size={14} />
              {busy ? "Verifying…" : "Enter the preview"}
            </button>
          </form>
        </div>

        <p
          style={{
            textAlign: "center",
            color: "rgba(251,250,253,0.50)",
            fontSize: 12,
            marginTop: 24,
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
          }}
        >
          © 2026 WHOLE Neighborhoods — preview build for civic partners.
        </p>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "rgba(251,250,253,0.06)",
  border: "1px solid rgba(251,250,253,0.22)",
  color: "#FBFAFD",
  padding: "12px 14px",
  fontSize: 14,
  borderRadius: 4,
  fontFamily: '"Nunito Sans", sans-serif',
  outline: "none",
  boxSizing: "border-box",
};
